import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ScrollText,
  Mail,
  Sparkles,
  Database,
  Send,
  AlertTriangle,
  Shield,
  Clock,
  Tag,
  Cpu,
  Link as LinkIcon,
  Copy,
  Check,
  Search,
  X
} from "lucide-react";
import { Card, CardBody } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { api } from "../lib/api";
import { cn } from "../lib/cn";
import { Pagination } from "../components/Pagination";

interface AuditEvent {
  eventId: string;
  eventType: string;
  caseId: string | null;
  actorType: "system" | "user" | "tenant_setup";
  actorId: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

interface PaginatedAudit {
  data: AuditEvent[];
  pagination: { nextCursor: string | null; hasMore: boolean; total: number };
}

const EVENT_TYPES: Array<{ value: string; label: string }> = [
  { value: "email.received", label: "Email received" },
  { value: "email.ignored", label: "Email ignored" },
  { value: "email.rejected", label: "Email rejected" },
  { value: "classification.matched", label: "Classification matched" },
  { value: "classification.parse_failed", label: "Classification failed" },
  { value: "slot.extracted", label: "Slot extracted" },
  { value: "slot.validation_failed", label: "Slot validation failed" },
  { value: "gather.clarification_sent", label: "Clarification sent" },
  { value: "gather.exhausted", label: "Gather exhausted" },
  { value: "reply.drafted", label: "Reply drafted" },
  { value: "reply.sent", label: "Reply sent" },
  { value: "reply.approved", label: "Reply approved" },
  { value: "reply.send_failed", label: "Reply send failed" },
  { value: "reply.guardrail_blocked", label: "Reply blocked (guardrail)" },
  { value: "connector.invoked", label: "Connector invoked" },
  { value: "connector.failed", label: "Connector failed" },
  { value: "tenant.dry_run_toggled", label: "Dry-run toggled" },
];

function getEventConfig(type: string) {
  if (type.startsWith("email.")) {
    const isError = type.includes("ignored") || type.includes("rejected");
    return {
      Icon: Mail,
      color: isError ? "text-rose-600 bg-rose-50 border-rose-200" : "text-blue-600 bg-blue-50 border-blue-200",
      label: type === "email.received" ? "Email received" : type === "email.ignored" ? "Email ignored" : "Email rejected",
    };
  }
  if (type.startsWith("classification.")) {
    const isError = type.includes("failed");
    return {
      Icon: Sparkles,
      color: isError ? "text-rose-600 bg-rose-50 border-rose-200" : "text-purple-600 bg-purple-50 border-purple-200",
      label: type === "classification.matched" ? "Category matched" : "Classification failed",
    };
  }
  if (type.startsWith("slot.")) {
    const isError = type.includes("failed");
    return {
      Icon: Tag,
      color: isError ? "text-amber-600 bg-amber-50 border-amber-200" : "text-indigo-600 bg-indigo-50 border-indigo-200",
      label: type === "slot.extracted" ? "Slot extracted" : "Slot validation failed",
    };
  }
  if (type.startsWith("gather.")) {
    const isError = type.includes("exhausted");
    return {
      Icon: Clock,
      color: isError ? "text-rose-600 bg-rose-50 border-rose-200" : "text-cyan-600 bg-cyan-50 border-cyan-200",
      label: type === "gather.clarification_sent" ? "Clarification sent" : "Gather limit reached",
    };
  }
  if (type.startsWith("reply.")) {
    const isError = type.includes("failed") || type.includes("blocked");
    const isSuccess = type === "reply.sent" || type === "reply.approved";
    return {
      Icon: Send,
      color: isError 
        ? "text-rose-600 bg-rose-50 border-rose-200" 
        : isSuccess 
          ? "text-emerald-600 bg-emerald-50 border-emerald-200" 
          : "text-slate-600 bg-slate-100 border-slate-200",
      label: type === "reply.drafted" ? "Reply drafted" : type === "reply.sent" ? "Reply sent" : type === "reply.approved" ? "Reply approved" : type === "reply.send_failed" ? "Send failed" : "Guardrail blocked",
    };
  }
  if (type.startsWith("connector.")) {
    const isError = type.includes("failed");
    return {
      Icon: Database,
      color: isError ? "text-rose-600 bg-rose-50 border-rose-200" : "text-amber-600 bg-amber-50 border-amber-200",
      label: type === "connector.invoked" ? "Connector invoked" : "Connector failed",
    };
  }
  return {
    Icon: Shield,
    color: "text-slate-600 bg-slate-50 border-slate-200",
    label: type,
  };
}

const DATE_RANGES: Array<{ value: string; label: string; hoursAgo: number | null }> = [
  { value: "24h", label: "Last 24 hours", hoursAgo: 24 },
  { value: "7d", label: "Last 7 days", hoursAgo: 24 * 7 },
  { value: "30d", label: "Last 30 days", hoursAgo: 24 * 30 },
  { value: "all", label: "All (30-day retention)", hoursAgo: null },
];

function formatDateTime(dateInput: string | Date): string {
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");
  return `${year}-${month}-${day} ${hoursStr}:${minutes} ${ampm}`;
}

function PayloadViewer({ payload }: { payload: Record<string, unknown> }) {
  const structuredData = useMemo(() => {
    const items: Array<{ label: string; value: string | number | boolean | null }> = [];
    
    const addIfExist = (key: string, label: string) => {
      if (payload[key] !== undefined && payload[key] !== null) {
        let val = payload[key];
        if (typeof val === "object") {
          val = JSON.stringify(val);
        }
        items.push({ label, value: String(val) });
      }
    };

    addIfExist("subject", "Subject");
    addIfExist("from", "From");
    addIfExist("fromEmail", "From Email");
    addIfExist("to", "To");
    addIfExist("reason", "Reason");
    addIfExist("rule", "Rule Name");
    addIfExist("intentId", "Intent ID");
    addIfExist("intentName", "Matched Category");
    addIfExist("confidence", "Confidence");
    addIfExist("slotName", "Slot Name");
    addIfExist("slotValue", "Slot Value");
    addIfExist("value", "Value");
    addIfExist("model", "Model Used");
    addIfExist("status", "Status Code");
    addIfExist("durationMs", "Duration (ms)");
    addIfExist("connectorId", "Connector");
    addIfExist("method", "HTTP Method");
    addIfExist("url", "Endpoint URL");
    addIfExist("messageId", "Message ID");
    addIfExist("recipient", "Recipient");

    return items;
  }, [payload]);

  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-3 space-y-4">
      {structuredData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {structuredData.map((item, idx) => (
            <div key={idx} className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 shadow-3xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {item.label}
              </span>
              <div className="mt-1 text-sm font-semibold text-slate-800 break-all font-sans">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Raw JSON panel */}
      <div className="rounded-2xl border border-slate-200 bg-slate-900 overflow-hidden shadow-xs">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-950/60">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">Payload JSON</span>
          <button
            type="button"
            onClick={copyToClipboard}
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-450" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy JSON
              </>
            )}
          </button>
        </div>
        <pre className="p-4 overflow-x-auto text-[11px] font-mono text-emerald-400 leading-relaxed max-h-96">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </div>
    </div>
  );
}

export default function Audit() {
  const [eventType, setEventType] = useState<string>("");
  const [caseIdFilter, setCaseIdFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("7d");
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<PaginatedAudit | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const sinceIso = useMemo(() => {
    const range = DATE_RANGES.find((r) => r.value === dateRange);
    if (!range || range.hoursAgo === null) return undefined;
    return new Date(Date.now() - range.hoursAgo * 60 * 60 * 1000).toISOString();
  }, [dateRange]);

  const fetchPage = useCallback(
    async (cursor: string | null, currentLimit: number) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", String(currentLimit));
        if (cursor) params.set("cursor", cursor);
        if (eventType) params.set("eventType", eventType);
        if (caseIdFilter.trim()) params.set("caseId", caseIdFilter.trim());
        if (sinceIso) params.set("since", sinceIso);
        const result = await api.getRaw<PaginatedAudit>(`/api/audit?${params.toString()}`);
        setData(result);
      } finally {
        setLoading(false);
      }
    },
    [eventType, caseIdFilter, sinceIso],
  );

  useEffect(() => {
    setCursors([null]);
    setPage(0);
    setExpanded({});
    fetchPage(null, limit);
  }, [limit, fetchPage]);

  const handlePageChange = async (targetPage: number) => {
    if (targetPage === page || loading) return;

    setExpanded({});
    if (targetPage < cursors.length) {
      setPage(targetPage);
      await fetchPage(cursors[targetPage] ?? null, limit);
    } else {
      setLoading(true);
      try {
        let currentCursor = data?.pagination.nextCursor;
        let tempCursors = [...cursors];
        let tempPage = page;
        let tempData = data;

        while (tempPage < targetPage && currentCursor) {
          const params = new URLSearchParams();
          params.set("limit", String(limit));
          if (currentCursor) params.set("cursor", currentCursor);
          if (eventType) params.set("eventType", eventType);
          if (caseIdFilter.trim()) params.set("caseId", caseIdFilter.trim());
          if (sinceIso) params.set("since", sinceIso);

          const result = await api.getRaw<PaginatedAudit>(`/api/audit?${params.toString()}`);
          tempCursors.push(currentCursor);
          currentCursor = result.pagination.nextCursor;
          tempPage++;
          tempData = result;
        }

        setCursors(tempCursors);
        setPage(tempPage);
        setData(tempData);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleRow = (id: string) => {
    setExpanded((m) => ({ ...m, [id]: !m[id] }));
  };

  const refresh = () => {
    setExpanded({});
    fetchPage(cursors[page] ?? null, limit);
  };

  const resetFilters = () => {
    setEventType("");
    setCaseIdFilter("");
    setDateRange("7d");
  };

  const rows = data?.data ?? [];


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Audit log</h1>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            Every system event Triage has recorded — for the last 30 days. Free-text emails and phone numbers in payloads are redacted at write time.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={refresh} disabled={loading} className="shrink-0 h-9.5 rounded-xl border-slate-200 shadow-3xs">
          <RefreshCw className={cn("mr-2 h-4 w-4", loading ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>


      {/* Search & Filters */}
      <Card className="border-slate-200/80 shadow-3xs">
        <CardBody className="p-5">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Search className="h-4.5 w-4.5 text-slate-400" />
                Filter Logs
              </span>
              {(eventType || caseIdFilter || dateRange !== "7d") && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1.5 text-xs font-bold text-rose-600 hover:text-rose-700 transition-colors cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear all filters
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">
                  Event type
                </label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/10 transition-all cursor-pointer"
                >
                  <option value="">All event types</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">
                  Date range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/10 transition-all cursor-pointer"
                >
                  {DATE_RANGES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">
                  Case ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filter by case ID..."
                    value={caseIdFilter}
                    onChange={(e) => setCaseIdFilter(e.target.value)}
                    className="block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 placeholder:text-gray-400/80 focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/10 transition-all"
                  />
                  {caseIdFilter && (
                    <button
                      type="button"
                      onClick={() => setCaseIdFilter("")}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Events List / Timeline */}
      {!data || (loading && rows.length === 0) ? (
        <Card className="border-slate-200/80 shadow-3xs">
          <CardBody className="py-12 flex flex-col items-center justify-center text-sm text-slate-500 font-semibold gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-brand-500" />
            Loading audit events...
          </CardBody>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="border-slate-200/80 shadow-3xs">
          <CardBody className="py-16 text-center text-sm text-slate-500">
            <ScrollText className="mx-auto mb-4 h-10 w-10 text-slate-350" />
            <p className="font-bold text-slate-800 text-base">No events found</p>
            <p className="mt-1 font-medium">Try widening the date range, changing filters, or checking back later.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="relative pl-6 space-y-4 before:absolute before:left-[10px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-100">
            {rows.map((evt) => {
              const id = evt.eventId;
              const isOpen = !!expanded[id];
              const config = getEventConfig(evt.eventType);
              const EventIcon = config.Icon;
              return (
                <div key={id} className="relative group">
                  {/* Timeline icon */}
                  <div className={cn(
                    "absolute -left-[30px] top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border bg-white shadow-2xs transition-all duration-200 group-hover:scale-105",
                    config.color
                  )}>
                    <EventIcon className="h-3.5 w-3.5" />
                  </div>

                  <div className={cn(
                    "rounded-2xl border border-slate-200 bg-white p-4.5 shadow-3xs transition-all duration-200 hover:shadow-2xs hover:border-slate-300/85",
                    isOpen ? "ring-1 ring-slate-250 shadow-2xs" : ""
                  )}>
                    <div
                      className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 cursor-pointer"
                      onClick={() => toggleRow(id)}
                    >
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="text-sm font-bold text-slate-800 hover:text-brand-600 transition-colors">
                          {config.label}
                        </span>
                        <span className="text-[10px] font-semibold text-slate-400 font-mono bg-slate-50 border border-slate-100 rounded-md px-1.5 py-0.5">
                          {evt.eventType}
                        </span>
                        {evt.caseId && (
                          <Link
                            to={`/cases/${evt.caseId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-brand-650 hover:text-brand-700 bg-brand-50/50 hover:bg-brand-55 border border-brand-100/50 px-2 py-0.5 rounded-lg transition-colors"
                          >
                            <LinkIcon className="h-2.5 w-2.5" />
                            {evt.caseId}
                          </Link>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-500 uppercase tracking-wide">
                          <Cpu className="h-2.5 w-2.5" />
                          {evt.actorType}{evt.actorId ? `:${evt.actorId}` : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 font-semibold md:ml-auto">
                        <Clock className="h-3.5 w-3.5 text-slate-350" />
                        <span>
                          {formatDateTime(evt.createdAt)}
                        </span>
                        {isOpen ? <ChevronDown className="h-4.5 w-4.5 ml-1 text-slate-400" /> : <ChevronRight className="h-4.5 w-4.5 ml-1 text-slate-400" />}
                      </div>
                    </div>

                    {!isOpen && (
                      <div className="mt-2 text-xs text-slate-500 font-mono truncate max-w-4xl cursor-pointer" onClick={() => toggleRow(id)}>
                        {payloadOneLine(evt.payload)}
                      </div>
                    )}

                    {isOpen && (
                      <PayloadViewer payload={evt.payload} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Pagination
            currentPage={page}
            totalRecords={data.pagination.total ?? 0}
            limit={limit}
            loading={loading}
            onPageChange={handlePageChange}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}
    </div>
  );
}

function payloadOneLine(p: Record<string, unknown>): string {
  const keys = Object.keys(p);
  if (keys.length === 0) return "(no payload)";
  return keys
    .slice(0, 6)
    .map((k) => `${k}=${stringifyShort(p[k])}`)
    .join(" · ");
}

function stringifyShort(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return v.length > 40 ? v.slice(0, 40) + "…" : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === "object") return "{…}";
  return String(v);
}
